import { useCallback, useEffect, useState } from 'react';
import type { ApprovalView } from '@alfred/shared';
import {
  api,
  describeError,
  type PostIdea,
  type PostsData,
  type TodoPlatform,
  type TodosData,
} from './api.js';
import { Approvals } from './Approvals.js';
import { VideoPlans } from './VideoPlans.js';

type Filter = 'all' | 'x' | 'reddit' | 'video';

const PLATFORM_LABEL: Record<TodoPlatform, string> = {
  x: 'X',
  reddit: 'Reddit',
  tiktok: 'TikTok',
  instagram: 'Reels',
  video: 'Video',
  other: 'Other',
};

/** Approval cards that are something to post (shared rule with the server's isPostDraft). */
export function isPostDraft(a: ApprovalView): boolean {
  const op = (a.input as { op?: unknown }).op;
  return (
    a.toolName === 'x-post' ||
    (['reddit', 'video'].includes(a.toolName) && op === 'posted') ||
    (a.toolName === 'x-trends' && op === 'replied')
  );
}

const platformOf = (a: ApprovalView): Filter =>
  a.toolName === 'x-post' || a.toolName === 'x-trends'
    ? 'x'
    : a.toolName === 'reddit'
      ? 'reddit'
      : 'video';

/**
 * Everything Alfred wants Anthony to post: today's to-do list, drafts ready to post (with the
 * post each one borrows from), ideas to draft from, and what he posted lately.
 */
export function PostsView({
  drafts,
  onChanged,
  onNotice,
}: {
  drafts: ApprovalView[];
  onChanged: () => Promise<void>;
  onNotice: (text: string) => void;
}) {
  const [data, setData] = useState<PostsData | null>(null);
  const [todos, setTodos] = useState<TodosData | null>(null);
  const [project, setProject] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  // Ideas whose script is being written (shown on the idea instead of its buttons).
  const [started, setStarted] = useState<Record<string, string>>({});
  const [newItem, setNewItem] = useState('');
  const [karma, setKarma] = useState('');

  const load = useCallback(async () => {
    const [p, t] = await Promise.allSettled([api.posts(), api.todos(project ?? undefined)]);
    if (p.status === 'fulfilled') setData(p.value);
    if (t.status === 'fulfilled') {
      setTodos(t.value);
      if (!project && t.value.project) setProject(t.value.project.id);
    }
  }, [project]);
  useEffect(() => void load(), [load]);

  /**
   * An idea's button. Feedback shows on the idea itself (the notice banner is often scrolled out
   * of view): a video plan runs in the background, so the idea says so until it's done; a
   * written draft scrolls into view in Ready to post.
   */
  const runIdea = async (i: PostIdea, kind: 'post' | 'reply') => {
    const key = `idea-${i.id}-${kind}`;
    const video = i.platform === 'tiktok' || i.platform === 'instagram';
    setBusy(key);
    try {
      await api.draftIdea({ project: i.project, platform: i.platform, id: i.id, kind });
      if (video) {
        setStarted((s) => ({
          ...s,
          [i.id]:
            'Writing you a script from this reel (about a minute). You’ll get a notification when it’s ready.',
        }));
        return;
      }
      await Promise.all([load(), onChanged()]);
      requestAnimationFrame(() =>
        document
          .querySelector('.posts-view .approvals')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    } catch (err) {
      onNotice(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  // While video plans are being made, check back every 10 seconds; a plan is done when its
  // idea leaves the list (ideas only show winners without a plan).
  const waiting = Object.keys(started).length > 0;
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [waiting, load]);
  useEffect(() => {
    if (!data) return;
    const listed = new Set(data.ideas.map((i) => i.id));
    const done = Object.keys(started).filter((id) => !listed.has(id));
    if (done.length === 0) return;
    setStarted((s) => Object.fromEntries(Object.entries(s).filter(([id]) => listed.has(id))));
    onNotice(
      done.length === 1
        ? 'Script ready ✓ It’s under Reel scripts below.'
        : `${done.length} scripts ready ✓ They’re under Reel scripts below.`,
    );
  }, [data, started, onNotice]);

  const act = async (key: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(key);
    try {
      await fn();
      if (done) onNotice(done);
      await Promise.all([load(), onChanged()]);
    } catch (err) {
      onNotice(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  const shown = (p: Filter) => filter === 'all' || filter === p;
  const ready = drafts.filter((d) => shown(platformOf(d)));
  const ideas = (data?.ideas ?? []).filter(
    (i) =>
      shown(i.platform === 'tiktok' || i.platform === 'instagram' ? 'video' : i.platform) &&
      (!project || i.project === project),
  );
  const posted = (data?.posted ?? []).filter((p) => shown(p.platform));
  const list = todos?.list;
  const items = (list?.items ?? []).filter(
    (i) =>
      filter === 'all' ||
      (filter === 'video' ? ['tiktok', 'instagram', 'video'] : [filter]).includes(i.platform),
  );
  const doneCount = list?.items.filter((i) => i.done).length ?? 0;
  const minutesLeft = list?.items.filter((i) => !i.done).reduce((a, i) => a + (i.minutes ?? 0), 0);
  const projectId = todos?.project?.id ?? project;
  const warm = todos?.reddit;

  return (
    <div className="view posts-view">
      <div className="posts-filters">
        {(data?.projects.length ?? 0) > 1 && (
          <select
            value={project ?? ''}
            aria-label="Project"
            onChange={(e) => setProject(e.target.value || null)}
          >
            {data?.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        {(['all', 'x', 'reddit', 'video'] as const).map((f) => (
          <button
            key={f}
            className={`chip-btn ${filter === f ? 'chip-btn-on' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'video' ? 'TikTok & Reels' : PLATFORM_LABEL[f]}
          </button>
        ))}
      </div>

      <section className="panel today">
        <h2 className="panel-title">
          Today{todos?.project ? ` · ${todos.project.name}` : ''}
          {list && (
            <span className="muted small">
              {' '}
              {doneCount}/{list.items.length} done
              {minutesLeft ? ` · ~${minutesLeft} min left` : ''}
            </span>
          )}
        </h2>
        {list?.summary && <p className="muted small">{list.summary}</p>}

        {warm && warm.active && (
          <div className="warmup small">
            <div>
              <strong>Reddit warm-up</strong>
              {warm.username ? ` (u/${warm.username})` : ''}: {warm.karma ?? '?'}/{warm.goal.karma}{' '}
              karma · day {warm.ageDays ?? '?'} of {warm.goal.days}. Drafts won’t mention your
              product until both are reached.
            </div>
            <div className="meter" aria-hidden="true">
              <span
                style={{ width: `${Math.min(100, ((warm.karma ?? 0) / warm.goal.karma) * 100)}%` }}
              />
            </div>
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                const n = Number(karma);
                if (!projectId || !Number.isFinite(n) || karma === '') return;
                void act(
                  'karma',
                  () => api.redditAccount({ project: projectId, karma: n }),
                  'Karma updated.',
                );
                setKarma('');
              }}
            >
              <input
                inputMode="numeric"
                placeholder="Karma now"
                value={karma}
                aria-label="Reddit karma now"
                onChange={(e) => setKarma(e.target.value.replace(/\D/g, ''))}
              />
              <button className="btn btn-small" disabled={busy !== null || !karma}>
                Update
              </button>
            </form>
          </div>
        )}

        {!list && (
          <div className="small">
            <p className="muted">Alfred writes your list at 8:30am.</p>
            {projectId && (
              <button
                className="btn btn-small"
                disabled={busy !== null}
                onClick={() => void act('write', () => api.writeTodos(projectId))}
              >
                {busy === 'write' ? 'Writing…' : 'Write today’s list now'}
              </button>
            )}
          </div>
        )}

        {items.length > 0 && (
          <ul className="todo-list">
            {items.map((i) => (
              <li key={i.id} className={i.done ? 'todo-done' : ''}>
                <label>
                  <input
                    type="checkbox"
                    checked={i.done}
                    disabled={busy !== null || !projectId}
                    onChange={(e) =>
                      projectId &&
                      void act(i.id, () =>
                        api.todoItem({ project: projectId, id: i.id, done: e.target.checked }),
                      )
                    }
                  />
                  <span>
                    <span className={`platform platform-${i.platform}`}>
                      {PLATFORM_LABEL[i.platform]}
                    </span>{' '}
                    {i.text}
                    {i.progress && !i.done ? (
                      <span className="muted">
                        {' '}
                        · {i.progress.done}/{i.progress.total} done
                      </span>
                    ) : null}
                    {i.minutes ? <span className="muted"> · {i.minutes} min</span> : null}
                    {i.why && <span className="muted small todo-why">{i.why}</span>}
                  </span>
                </label>
                {(i.approvalId ?? i.approvalIds?.[0]) && !i.done && (
                  <a className="small" href={`#draft-${i.approvalId ?? i.approvalIds?.[0]}`}>
                    {i.approvalIds?.length ? 'See drafts ↓' : 'See draft ↓'}
                  </a>
                )}
                {i.link && (
                  <a className="small" href={i.link} target="_blank" rel="noreferrer">
                    Open ↗
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}

        {projectId && list && (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newItem.trim()) return;
              void act('add', () => api.todoItem({ project: projectId, text: newItem.trim() }));
              setNewItem('');
            }}
          >
            <input
              placeholder="Add something to today’s list"
              value={newItem}
              aria-label="New to-do item"
              onChange={(e) => setNewItem(e.target.value)}
            />
            <button className="btn btn-small" disabled={busy !== null || !newItem.trim()}>
              Add
            </button>
          </form>
        )}
      </section>

      {ready.length > 0 ? (
        <Approvals items={ready} onChanged={onChanged} onNotice={onNotice} title="Ready to post" />
      ) : (
        <section className="panel">
          <h2 className="panel-title">Ready to post</h2>
          <p className="muted small">Nothing waiting. Drafts arrive after the morning scans.</p>
        </section>
      )}

      {ideas.length > 0 && (
        <section className="panel">
          <h2 className="panel-title">Ideas ({ideas.length})</h2>
          <p className="muted small">
            Winners Alfred found that don’t have a draft yet. Open the original first. “My own post”
            writes a new post of yours in that format; “Reply” writes a reply under theirs.
          </p>
          <ul className="idea-list">
            {ideas.map((i) => (
              <li key={`${i.platform}-${i.id}`}>
                <div className="small">
                  <span className={`platform platform-${i.platform}`}>
                    {PLATFORM_LABEL[i.platform]}
                  </span>{' '}
                  <a href={i.url} target="_blank" rel="noreferrer">
                    {i.where} ↗
                  </a>{' '}
                  <span className="muted">· {i.numbers}</span>
                </div>
                <blockquote>{i.text}</blockquote>
                {i.why && <div className="muted small">{i.why}</div>}
                {started[i.id] ? (
                  <p className="idea-status small" role="status">
                    ⏳ {started[i.id]}
                  </p>
                ) : (
                  <div className="idea-actions">
                    {(i.platform === 'x' || i.platform === 'reddit'
                      ? (['post', 'reply'] as const)
                      : (['post'] as const)
                    ).map((kind) => (
                      <button
                        key={kind}
                        className="btn btn-small"
                        disabled={busy === `idea-${i.id}-${kind}`}
                        onClick={() => void runIdea(i, kind)}
                      >
                        {busy === `idea-${i.id}-${kind}`
                          ? 'Writing…'
                          : i.platform === 'tiktok' || i.platform === 'instagram'
                            ? 'Write me a script'
                            : kind === 'post'
                              ? 'Write my own post like this'
                              : i.platform === 'x'
                                ? `Write a reply to ${i.where}`
                                : 'Write a reply in the thread'}
                      </button>
                    ))}
                    <button
                      className="btn btn-small btn-quiet"
                      disabled={busy === `idea-${i.id}-remove`}
                      onClick={() =>
                        void act(`idea-${i.id}-remove`, () =>
                          api.dismissIdea({ project: i.project, platform: i.platform, id: i.id }),
                        )
                      }
                    >
                      {busy === `idea-${i.id}-remove` ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {shown('video') && (
        <VideoPlans
          plans={(data?.plans ?? []).filter((p) => !project || p.project === project)}
          projectName={todos?.project?.name ?? 'this project'}
          onChanged={load}
          onNotice={onNotice}
        />
      )}

      {posted.length > 0 && (
        <section className="panel">
          <h2 className="panel-title">Posted lately</h2>
          <ul className="posted-list">
            {posted.map((p) => (
              <li key={p.approvalId} className="small">
                <span className={`platform platform-${p.platform}`}>
                  {PLATFORM_LABEL[p.platform]}
                </span>{' '}
                {p.url ? (
                  <a href={p.url} target="_blank" rel="noreferrer">
                    {p.summary} ↗
                  </a>
                ) : (
                  p.summary
                )}
                <div className="muted">
                  {new Date(p.postedAt).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                  {p.numbers ? ` · ${p.numbers}` : ''}
                  {p.inspiration && (
                    <>
                      {' · copied '}
                      <a href={p.inspiration.url} target="_blank" rel="noreferrer">
                        {p.inspiration.label}
                      </a>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
